import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import UserActivityFilters from './UserActivityFilters';
import { LAST_LOGIN_ANY } from './userActivityFormat';
import UserActivityTable from './UserActivityTable';
import UserActivityTiles from './UserActivityTiles';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

const SEARCH_DELAY_MS = 300;

/**
 * The filters as query parameters. The table and the download read the same
 * object, so a sheet always matches the table it was downloaded from.
 */
function queryFor(filters) {
    const params = {};

    if (filters.search.trim() !== '') {
        params.search = filters.search.trim();
    }

    if (filters.roleId) {
        params.role_id = filters.roleId;
    }

    if (filters.lastLogin !== LAST_LOGIN_ANY) {
        params.last_login = filters.lastLogin;
    }

    return params;
}

export default function UserActivityIndex({ dataUrl, historyUrl, exportUrl }) {
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({ search: '', roleId: '', lastLogin: LAST_LOGIN_ANY });
    const [page, setPage] = useState(1);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedIds, setExpandedIds] = useState([]);

    const readSeq = useRef(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((previous) => (previous.search === searchInput ? previous : { ...previous, search: searchInput }));
            setPage(1);
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        axios
            .get(dataUrl, { params: { ...queryFor(filters), page } })
            .then(({ data }) => {
                // Only the newest read may land, so the table never lags the
                // filters above it.
                if (sequence === readSeq.current) {
                    setRows(data.data);
                    setMeta(data.meta);
                    setExpandedIds([]);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [dataUrl, filters, page]);

    const changeFilters = useCallback((change) => {
        setFilters((previous) => ({ ...previous, ...change }));
        setPage(1);
    }, []);

    const toggleRow = useCallback((id) => {
        setExpandedIds((previous) => (previous.includes(id) ? previous.filter((expandedId) => expandedId !== id) : [...previous, id]));
    }, []);

    const download = useCallback(() => {
        const params = new URLSearchParams(queryFor(filters));
        const query = params.toString();

        window.location.assign(query ? `${exportUrl}?${query}` : exportUrl);
    }, [exportUrl, filters]);

    return (
        <div className="table__card tw-space-y-4">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                <h2 className="table__header tw-mb-0">{$t('User activity')}</h2>
                <Button variant="outline" onClick={download}>
                    {$t('Download')}
                </Button>
            </div>

            {error && <Alert type="error">{error}</Alert>}

            <UserActivityTiles tiles={meta?.tiles} />

            <p className="tw-rounded-lg tw-bg-blue-15 tw-px-4 tw-py-3 tw-text-xs tw-text-charcoal-85 tw-mb-0" role="status">
                {$t(
                    'Shows the same invite and login data as the organisation Users page, limited to this project, so Event Admins without organisation access can see who has started using it.',
                )}{' '}
                {/* The organisation's own retention setting; null keeps everything. */}
                {meta &&
                    (meta.retention_months
                        ? $t('History is kept for {{months}} months.', { months: meta.retention_months })
                        : $t('History is kept for the life of the organisation, not 30 days.'))}
            </p>

            <UserActivityFilters
                search={searchInput}
                roleId={filters.roleId}
                lastLogin={filters.lastLogin}
                roles={meta?.roles ?? []}
                onSearchChange={setSearchInput}
                onChange={changeFilters}
            />

            {loading && !meta ? (
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>
            ) : (
                <UserActivityTable rows={rows} expandedIds={expandedIds} onToggle={toggleRow} historyUrl={historyUrl} />
            )}

            {meta && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">{$t('Expand a row to see every login for that user on this project.')}</p>
                    {meta.last_page > 1 && (
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <PaginationInfo from={meta.from} to={meta.to} total={meta.total} />
                            <TablePagination lastPage={meta.last_page} currentPage={meta.current_page} pageChange={({ selected }) => setPage(selected + 1)} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

if (document.getElementById('userActivity')) {
    const rootEl = document.getElementById('userActivity');

    ReactDOM.render(
        <App>
            <UserActivityIndex
                dataUrl={rootEl.getAttribute('data-url')}
                historyUrl={rootEl.getAttribute('data-history-url')}
                exportUrl={rootEl.getAttribute('data-export-url')}
            />
        </App>,
        rootEl,
    );
}
